"use client";

import { formatImageUrl } from "@/lib/utils";
import { TPostLink, TPostMedia } from "@/types/post";
import { Editor } from "@tiptap/react";
import { createContext, ReactNode, useCallback, useContext, useState } from "react";

export type TEditorType = "discussion" | "article" | "comment" | "basic" | "advanced";

interface CustomEditorContextType {
	// Media handling
	openMediaUploader: (inline: boolean) => void;
	mediaUploaderState: MediaUploaderState;
	closeMediaUploader: () => void;

	// Link card handling
	linkCards: TPostLink[];
	addLinkCard: (url: string) => void;
	removeLinkCard: (url: string) => void;

	// Post type
	editorType: TEditorType;
	setEditorType: (editorType: TEditorType) => void;

	editor: Editor | null;
	setEditor: (editor: Editor | null) => void;

	images: Array<Omit<TPostMedia, "id">>;
	setImages: (images: Array<Omit<TPostMedia, "id">>) => void;

	addInlineMedia: (image: Array<Omit<TPostMedia, "id">>) => void;

	imageViewType: "grid" | "carousel";
	setImageViewType: (type: "grid" | "carousel") => void;

	isLinkUploaderOpen: boolean;
	openLinkUploader: () => void;
	closeLinkUploader: () => void;
	clearContent: () => void;

	getHtml: () => string | undefined;
}

export interface MediaUploaderState {
	isOpen: boolean;
	isInline: boolean;
}

const EditorContext = createContext<CustomEditorContextType | undefined>(undefined);

function CustomEditorProvider({
	defaultValue,
	children,
}: {
	defaultValue?: { editorType?: TEditorType; links?: TPostLink[]; images?: TPostMedia[] };
	children: ReactNode;
}) {
	const [editorType, setEditorType] = useState<TEditorType>(
		defaultValue?.editorType || "discussion"
	);
	const [mediaUploaderState, setMediaUploaderState] = useState<MediaUploaderState>({
		isOpen: false,
		isInline: false,
	});
	const [linkCards, setLinkCards] = useState<TPostLink[]>(defaultValue?.links ?? []);
	const [editor, setEditorInstance] = useState<Editor | null>(null);
	const [images, setImages] = useState<Array<Omit<TPostMedia, "id">>>(defaultValue?.images ?? []);
	const [imageViewType, setImageViewType] = useState<"grid" | "carousel">("carousel");

	const [isLinkUploaderOpen, setIsLinkUploaderOpen] = useState(false);

	const clearContent = useCallback(() => {
		editor?.commands.clearContent();
	}, [editor]);

	const setEditor = useCallback((newEditor: Editor | null) => {
		setEditorInstance(newEditor);
	}, []);

	const openLinkUploader = useCallback(() => {
		setIsLinkUploaderOpen(true);
	}, []);

	const closeLinkUploader = useCallback(() => {
		setIsLinkUploaderOpen(false);
	}, []);

	const openMediaUploader = useCallback((inline: boolean) => {
		setMediaUploaderState({ isInline: inline, isOpen: true });
	}, []);

	const closeMediaUploader = useCallback(() => {
		setMediaUploaderState({ isInline: false, isOpen: false });
	}, []);

	const addInlineMedia = useCallback(
		(images: Array<Omit<TPostMedia, "id">>) => {
			console.log(images.length);
			editor
				?.chain()
				.insertContent(
					images.map((e) => ({
						type: "customImage",
						attrs: { alt: e.alt || "", src: formatImageUrl(e.objectPath), isCover: false },
					}))
				)
				.run();
		},
		[editor]
	);

	const addLinkCard = useCallback(async (url: string) => {
		try {
			const response = await fetch(`/api/metadata?url=${encodeURIComponent(url)}`);

			const { data } = await response.json();

			if (response.ok) {
				const newCard: TPostLink = {
					url,
					title: data.title,
					description: data.description,
					image: data.image,
				};

				setLinkCards((prev) => [...prev, newCard]);

				// Set image view type to carousel when a link card is added
				setImageViewType("carousel");
			}
		} catch (error) {
			console.error("Failed to add link card:", error);
		}
	}, []);

	const removeLinkCard = useCallback((url: string) => {
		setLinkCards((prev) => prev.filter((card) => card.url !== url));
	}, []);

	return (
		<EditorContext.Provider
			value={{
				getHtml: () => editor?.getHTML(),
				clearContent,
				addInlineMedia,
				openMediaUploader,
				mediaUploaderState,
				closeMediaUploader,
				linkCards,
				addLinkCard,
				removeLinkCard,
				editor,
				setEditor,
				editorType,
				setEditorType,
				images,
				setImages,
				imageViewType,
				setImageViewType,
				isLinkUploaderOpen,
				openLinkUploader,
				closeLinkUploader,
			}}
		>
			{children}
		</EditorContext.Provider>
	);
}

export const useCustomEditor = () => {
	const context = useContext(EditorContext);

	if (context === undefined) {
		throw new Error("useCustomEditor must be used within an CustomEditorProvider");
	}
	return context;
};

export default CustomEditorProvider;
